// Copyright (c) Ping. All rights reserved.
//
// Best-effort DeviceGroup mirror for the Tasks surface. Tasks are
// DeviceGroup-only personal state (§2.10 `0x92 TASK_UPSERT` / `0x93
// TASK_DELETE`): the local write through `webTasksPersister` is the
// source of truth and ALWAYS happens first (so the surface works fully
// offline / before the SDK is ready); this helper additionally attempts
// to broadcast the change to the user's OTHER devices over the
// DeviceGroup conversation so the list stays mirrored cross-device.
//
// Resolution: the DeviceGroup conversation is the single entry in the
// SDK's `listConversations()` with `is_device_group === true`. Its id
// bytes are stringified with `bytesToHex` — the same canonical form
// `getOrCreateMlsConversation` (via `resolveActiveConv`) keys MLS state
// on, so the existing chat-send plumbing resolves it for free.
//
// Every step is wrapped so a send failure NEVER surfaces to the caller:
// the local write already succeeded and the receiving devices reconcile
// last-writer-wins on the next successful sync.

import { encodeEnvelopeJsonBytes, type TaskRecord } from '@ping/ui';
import { bytesToHex } from '@ping/ui/src/conversation/mlsBytes';

import { log } from '@core/log/log';
import { resolveActiveConv } from '@features/chat/resolveActiveConv';

// Local mirror of the foundations `TaskUpsertBody` wire shape (§2.10
// `0x92 TASK_UPSERT`). Not re-exported from the `@ping/ui` barrel, so
// duplicated here field-for-field; the decoder in
// `appEvent/envelope.ts` reads exactly these keys.
interface TaskUpsertWire {
  readonly task_id: string;
  readonly title: string;
  readonly position: string;
  readonly due?: number;
  readonly done?: boolean;
  readonly list?: string;
  // Richer fields (additive; older devices ignore unknown keys and
  // older rows omit them, defaulting on the receiver).
  readonly priority?: 'low' | 'medium' | 'high' | 'urgent';
  readonly state?: 'backlog' | 'pending' | 'inProgress' | 'completed' | 'cancelled';
  readonly assignee_id?: string;
  readonly description?: string;
  // Exact cross-device LWW key — receivers prefer this over message receive
  // time (matches the note_upsert wire + the iOS task sender).
  readonly updated_at?: number;
}

// The DeviceGroup conversation, when present, is the sole entry with
// `is_device_group === true`. The id is the only field we consume.
interface SdkClientLike {
  listConversations?: () => Promise<Array<{ id: Uint8Array; is_device_group: boolean }>>;
}

async function resolveDeviceGroupConvId(client: unknown): Promise<string | null> {
  const sdk = client as SdkClientLike;
  if (typeof sdk.listConversations !== 'function') return null;
  let convs: Array<{ id: Uint8Array; is_device_group: boolean }>;
  try {
    convs = await sdk.listConversations();
  } catch (err) {
    log.warn({
      event: 'tasks.deviceGroup.list_failed',
      message: (err as Error).message,
    });
    return null;
  }
  const dg = convs.find((c) => c.is_device_group);
  return dg ? bytesToHex(dg.id) : null;
}

/**
 * Mirror a task upsert to the DeviceGroup. No-op (logged) when there is
 * no client or no DeviceGroup conversation yet — the local persister
 * write the caller already performed remains authoritative.
 */
export async function sendTaskUpsert(client: unknown | null, record: TaskRecord): Promise<void> {
  if (client === null) return;
  try {
    const conversationId = await resolveDeviceGroupConvId(client);
    if (conversationId === null) {
      log.info({ event: 'tasks.deviceGroup.upsert.no_dg' });
      return;
    }
    const conv = await resolveActiveConv({ client, conversationId, conversationName: undefined });
    if (conv === null) return;
    const payload: TaskUpsertWire = {
      task_id: record.id,
      title: record.title,
      position: record.position,
      ...(record.due !== undefined ? { due: record.due } : {}),
      ...(record.done !== undefined ? { done: record.done } : {}),
      ...(record.list !== undefined ? { list: record.list } : {}),
      ...(record.priority !== undefined ? { priority: record.priority } : {}),
      ...(record.state !== undefined ? { state: record.state } : {}),
      ...(record.assigneeId !== undefined ? { assignee_id: record.assigneeId } : {}),
      ...(record.description !== undefined ? { description: record.description } : {}),
      ...(record.updatedAt !== undefined ? { updated_at: record.updatedAt } : {}),
    };
    await conv.send(
      encodeEnvelopeJsonBytes({
        kind: 'com.ping.task_upsert',
        payload: payload as unknown as Record<string, unknown>,
      }),
    );
    log.info({ event: 'tasks.deviceGroup.upsert.sent', taskIdPrefix: record.id.slice(0, 8) });
  } catch (err) {
    log.warn({ event: 'tasks.deviceGroup.upsert.failed', message: (err as Error).message });
  }
}

/**
 * Nudge the user's OTHER devices to re-fetch the shared profile after a
 * name/avatar change, so it updates in realtime instead of only on their next
 * foreground. Carries no payload — `/v1/me/profile` is the source of truth.
 * Same best-effort DeviceGroup contract as the task mirrors above.
 */
export async function sendSelfProfileChanged(client: unknown | null): Promise<void> {
  if (client === null) return;
  try {
    const conversationId = await resolveDeviceGroupConvId(client);
    if (conversationId === null) {
      log.info({ event: 'profile.deviceGroup.no_dg' });
      return;
    }
    const conv = await resolveActiveConv({ client, conversationId, conversationName: undefined });
    if (conv === null) return;
    await conv.send(
      encodeEnvelopeJsonBytes({ kind: 'com.ping.self_profile', payload: {} }),
    );
    log.info({ event: 'profile.deviceGroup.sent' });
  } catch (err) {
    log.warn({ event: 'profile.deviceGroup.failed', message: (err as Error).message });
  }
}

/**
 * Mirror a per-conversation personal-state change (mute / archive / pin /
 * favorite / delete / folder) to the user's OTHER devices over the DeviceGroup.
 * The local Dexie write the caller already performed is authoritative; this just
 * propagates it. `patch` is the field/value set to apply on each sibling (e.g.
 * `{ isMuted: true }`, `{ archivedAt: 0 }`, `{ favorite: true }`,
 * `{ folderId: 'work' | null }`). Best-effort — never throws.
 */
export async function sendConvState(
  client: unknown | null,
  targetConversationId: string,
  patch: Record<string, unknown>,
): Promise<void> {
  if (client === null) return;
  try {
    const conversationId = await resolveDeviceGroupConvId(client);
    if (conversationId === null) {
      log.info({ event: 'convState.deviceGroup.no_dg' });
      return;
    }
    // `quiet: true` — this DeviceGroup mirror is best-effort (the caller's local
    // write is authoritative; siblings reconcile on their next sync), so a stale/
    // absent DeviceGroup MLS group must degrade SILENTLY rather than spewing
    // `cache_stale`/`list_failed`/`invite.no_local_row` at error/warn.
    const conv = await resolveActiveConv({
      client,
      conversationId,
      conversationName: undefined,
      quiet: true,
    });
    if (conv === null) return;
    await conv.send(
      encodeEnvelopeJsonBytes({
        kind: 'com.ping.conv_state',
        payload: { conversation_id: targetConversationId, patch },
      }),
    );
    log.info({
      event: 'convState.deviceGroup.sent',
      targetPrefix: targetConversationId.slice(0, 8),
    });
  } catch (err) {
    // Best-effort: a missing/stale DeviceGroup (e.g. single-device account, or MLS
    // state not restored this session) is expected — DEBUG, not warn.
    log.debug({ event: 'convState.deviceGroup.failed', message: (err as Error).message });
  }
}

/**
 * Mirror a "product tour seen" completion to the user's OTHER devices over the
 * DeviceGroup, so finishing a guided tour on one device suppresses it on the
 * rest. The local settings_kv flag the caller already wrote is authoritative;
 * this just propagates it. Best-effort — never throws.
 */
export async function sendTourSeen(client: unknown | null, tourId: string): Promise<void> {
  if (client === null) return;
  try {
    const conversationId = await resolveDeviceGroupConvId(client);
    if (conversationId === null) {
      log.info({ event: 'tour.deviceGroup.no_dg' });
      return;
    }
    // `quiet: true` — best-effort DeviceGroup mirror (the caller's local flag is
    // authoritative; siblings reconcile on their next sync), so a stale/absent
    // DeviceGroup MLS group degrades silently rather than spewing warnings.
    const conv = await resolveActiveConv({
      client,
      conversationId,
      conversationName: undefined,
      quiet: true,
    });
    if (conv === null) return;
    await conv.send(
      encodeEnvelopeJsonBytes({
        kind: 'com.ping.tour_seen',
        payload: { tour_id: tourId, seen_at: Date.now() },
      }),
    );
    log.info({ event: 'tour.deviceGroup.sent', tourId });
  } catch (err) {
    log.debug({ event: 'tour.deviceGroup.failed', message: (err as Error).message });
  }
}

/**
 * Star / unstar a message across the user's devices (§2.7 `0x64`, DeviceGroup-
 * only). `target` is the message content hash (hex); `starred:false` unstars
 * (no UNSTAR kind). Best-effort — the local optimistic write is authoritative.
 */
export async function sendStar(
  client: unknown | null,
  target: string,
  starred: boolean,
): Promise<void> {
  if (client === null) return;
  try {
    const conversationId = await resolveDeviceGroupConvId(client);
    if (conversationId === null) {
      log.info({ event: 'star.deviceGroup.no_dg' });
      return;
    }
    const conv = await resolveActiveConv({ client, conversationId, conversationName: undefined, quiet: true });
    if (conv === null) return;
    await conv.send(
      encodeEnvelopeJsonBytes({ kind: 'com.ping.star', payload: { target, starred } }),
    );
    log.info({ event: 'star.deviceGroup.sent', targetPrefix: target.slice(0, 8), starred });
  } catch (err) {
    log.debug({ event: 'star.deviceGroup.failed', message: (err as Error).message });
  }
}

/** Mirror a task delete to the DeviceGroup. Same best-effort contract. */
export async function sendTaskDelete(client: unknown | null, taskId: string): Promise<void> {
  if (client === null) return;
  try {
    const conversationId = await resolveDeviceGroupConvId(client);
    if (conversationId === null) {
      log.info({ event: 'tasks.deviceGroup.delete.no_dg' });
      return;
    }
    const conv = await resolveActiveConv({ client, conversationId, conversationName: undefined });
    if (conv === null) return;
    await conv.send(
      encodeEnvelopeJsonBytes({
        kind: 'com.ping.task_delete',
        payload: { task_id: taskId },
      }),
    );
    log.info({ event: 'tasks.deviceGroup.delete.sent', taskIdPrefix: taskId.slice(0, 8) });
  } catch (err) {
    log.warn({ event: 'tasks.deviceGroup.delete.failed', message: (err as Error).message });
  }
}
